import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { VehicleGallery } from '@/components/VehicleGallery';
import { WysiwygEditor } from '@/components/WysiwygEditor';
import { 
  Car, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  Eye, 
  EyeOff,
  ArrowLeft,
  Loader2,
  Calendar,
  FileText,
  Upload,
  Images,
  Image as ImageIcon,
  Link as LinkIcon,
  ExternalLink,
  Hash,
  AlignLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VehiclesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const fileInputRefs = useRef({});
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    year: '',
    description: '',
    detailed_description: '',
    vin: '',
    external_link: '',
    is_public: true
  });

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/profile/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Bitte Fahrzeugname eingeben');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/profile/vehicles`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVehicles([...vehicles, response.data.vehicle]);
      setFormData({ name: '', year: '', description: '', detailed_description: '', vin: '', external_link: '', is_public: true });
      setShowAddForm(false);
      toast.success('Fahrzeug hinzugefügt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (vehicleId) => {
    setSaving(true);
    try {
      const token = getToken();
      await axios.put(
        `${API_URL}/api/profile/vehicles/${vehicleId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVehicles(vehicles.map(v => 
        v.id === vehicleId ? { ...v, ...formData } : v
      ));
      setEditingId(null);
      toast.success('Fahrzeug aktualisiert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Fahrzeug wirklich löschen?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API_URL}/api/profile/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVehicles(vehicles.filter(v => v.id !== vehicleId));
      toast.success('Fahrzeug gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const startEdit = (vehicle) => {
    setFormData({
      name: vehicle.name || '',
      year: vehicle.year || '',
      description: vehicle.description || '',
      detailed_description: vehicle.detailed_description || '',
      vin: vehicle.vin || '',
      external_link: vehicle.external_link || '',
      is_public: vehicle.is_public !== false
    });
    setEditingId(vehicle.id);
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', year: '', description: '', detailed_description: '', vin: '', external_link: '', is_public: true });
  };

  const toggleVisibility = async (vehicle) => {
    try {
      const token = getToken();
      const newVisibility = !vehicle.is_public;
      
      await axios.put(
        `${API_URL}/api/profile/vehicles/${vehicle.id}`,
        { is_public: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVehicles(vehicles.map(v => 
        v.id === vehicle.id ? { ...v, is_public: newVisibility } : v
      ));
      
      toast.success(newVisibility ? 'Fahrzeug ist jetzt sichtbar' : 'Fahrzeug ist jetzt privat');
    } catch (error) {
      toast.error('Fehler beim Ändern der Sichtbarkeit');
    }
  };

  const handleImageUpload = async (vehicleId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nur Bilder erlaubt (JPEG, PNG, WebP, GIF)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bild zu gross (max. 10MB)');
      return;
    }
    
    setUploadingImage(vehicleId);
    
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/profile/vehicles/${vehicleId}/images`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      // Add the new image to the vehicle's images array
      setVehicles(vehicles.map(v => {
        if (v.id === vehicleId) {
          const currentImages = v.images || [];
          return { ...v, images: [...currentImages, response.data.image] };
        }
        return v;
      }));
      
      toast.success('Bild hochgeladen');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Hochladen';
      toast.error(msg);
    } finally {
      setUploadingImage(null);
    }
  };

  const handleImageDelete = async (vehicleId, imageId) => {
    if (!window.confirm('Bild wirklich löschen?')) return;
    
    setUploadingImage(vehicleId);
    
    try {
      const token = getToken();
      await axios.delete(
        `${API_URL}/api/profile/vehicles/${vehicleId}/images/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove the image from the vehicle's images array
      setVehicles(vehicles.map(v => {
        if (v.id === vehicleId) {
          return { ...v, images: (v.images || []).filter(img => img.id !== imageId) };
        }
        return v;
      }));
      
      toast.success('Bild gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setUploadingImage(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className=" flex items-center justify-center min-h-[50vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className=" max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-none"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide">
              Meine Fahrzeuge
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Verwalten Sie Ihre Fahrzeugsammlung
            </p>
          </div>
        </div>

        {/* Add Button */}
        {!showAddForm && !editingId && (
          <Button
            onClick={() => setShowAddForm(true)}
            className="mb-6 rounded-none"
          >
            <Plus size={18} className="mr-2" />
            Fahrzeug hinzufügen
          </Button>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="bg-card border border-border p-6 mb-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Car className="text-primary" size={20} />
              Neues Fahrzeug
            </h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Fahrzeugname *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="z.B. Porsche 911 Carrera RS 2.7"
                    className="mt-1 rounded-none"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Baujahr
                  </Label>
                  <Input
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: e.target.value})}
                    placeholder="z.B. 1973"
                    className="mt-1 rounded-none"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Kurzbeschreibung
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Kurze Zusammenfassung des Fahrzeugs..."
                  className="mt-1 rounded-none"
                  rows={2}
                  maxLength={200}
                />
                <p className="text-xs text-muted-foreground mt-1">{formData.description?.length || 0}/200 Zeichen</p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <AlignLeft size={12} />
                  Detailbeschreibung
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Ausführliche Informationen, Geschichte, technische Details...
                </p>
                <WysiwygEditor
                  value={formData.detailed_description}
                  onChange={(html) => setFormData({...formData, detailed_description: html})}
                  placeholder="Ausführliche Beschreibung des Fahrzeugs..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Hash size={12} />
                    Fahrgestellnummer (VIN/S/N)
                  </Label>
                  <Input
                    value={formData.vin}
                    onChange={(e) => setFormData({...formData, vin: e.target.value})}
                    placeholder="z.B. WP0ZZZ91ZJS100123"
                    className="mt-1 rounded-none font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <LinkIcon size={12} />
                    Link zum Fahrzeug
                  </Label>
                  <Input
                    value={formData.external_link}
                    onChange={(e) => setFormData({...formData, external_link: e.target.value})}
                    placeholder="z.B. https://www.classicdriver.com/..."
                    className="mt-1 rounded-none text-sm"
                    type="url"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-secondary/30 border border-border">
                <input
                  type="checkbox"
                  id="is_public_new"
                  checked={formData.is_public}
                  onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="is_public_new" className="text-sm flex items-center gap-2 cursor-pointer">
                  {formData.is_public ? <Eye size={16} /> : <EyeOff size={16} />}
                  <span>
                    {formData.is_public 
                      ? 'Für andere Mitglieder sichtbar' 
                      : 'Nur für mich sichtbar (privat)'}
                  </span>
                </label>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving} className="rounded-none">
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
                  Speichern
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowAddForm(false);
                    setFormData({ name: '', year: '', description: '', detailed_description: '', vin: '', external_link: '', is_public: true });
                  }}
                  className="rounded-none"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Vehicles List */}
        {vehicles.length === 0 && !showAddForm ? (
          <div className="text-center py-12 text-muted-foreground">
            <Car size={48} className="mx-auto mb-4 opacity-50" />
            <p>Sie haben noch keine Fahrzeuge hinzugefügt.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {vehicles.map((vehicle) => (
              <div 
                key={vehicle.id}
                className="bg-card border border-border p-4 sm:p-5"
              >
                {editingId === vehicle.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                          Fahrzeugname *
                        </Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="mt-1 rounded-none"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                          Baujahr
                        </Label>
                        <Input
                          value={formData.year}
                          onChange={(e) => setFormData({...formData, year: e.target.value})}
                          className="mt-1 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Kurzbeschreibung
                      </Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="mt-1 rounded-none"
                        rows={2}
                        maxLength={200}
                      />
                      <p className="text-xs text-muted-foreground mt-1">{formData.description?.length || 0}/200 Zeichen</p>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        <AlignLeft size={12} />
                        Detailbeschreibung
                      </Label>
                      <WysiwygEditor
                        value={formData.detailed_description}
                        onChange={(html) => setFormData({...formData, detailed_description: html})}
                        placeholder="Ausführliche Beschreibung..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                          <Hash size={12} />
                          Fahrgestellnummer (VIN/S/N)
                        </Label>
                        <Input
                          value={formData.vin}
                          onChange={(e) => setFormData({...formData, vin: e.target.value})}
                          placeholder="z.B. WP0ZZZ91ZJS100123"
                          className="mt-1 rounded-none font-mono text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                          <LinkIcon size={12} />
                          Link zum Fahrzeug
                        </Label>
                        <Input
                          value={formData.external_link}
                          onChange={(e) => setFormData({...formData, external_link: e.target.value})}
                          placeholder="z.B. https://www.classicdriver.com/..."
                          className="mt-1 rounded-none text-sm"
                          type="url"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-secondary/30 border border-border">
                      <input
                        type="checkbox"
                        id={`is_public_${vehicle.id}`}
                        checked={formData.is_public}
                        onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <label htmlFor={`is_public_${vehicle.id}`} className="text-sm flex items-center gap-2 cursor-pointer">
                        {formData.is_public ? <Eye size={16} /> : <EyeOff size={16} />}
                        <span>
                          {formData.is_public 
                            ? 'Für andere Mitglieder sichtbar' 
                            : 'Nur für mich sichtbar (privat)'}
                        </span>
                      </label>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => handleUpdate(vehicle.id)} 
                        disabled={saving}
                        className="rounded-none"
                      >
                        {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
                        Speichern
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={cancelEdit}
                        className="rounded-none"
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex flex-col gap-4">
                    {/* Vehicle Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Car className="text-primary flex-shrink-0" size={24} />
                          <h3 className="font-semibold text-lg">{vehicle.name}</h3>
                          {vehicle.year && (
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Calendar size={14} />
                              {vehicle.year}
                            </span>
                          )}
                        </div>
                        
                        {vehicle.description && (
                          <p className="text-sm text-muted-foreground ml-9 mb-3">
                            {vehicle.description}
                          </p>
                        )}

                        {/* VIN and External Link */}
                        {(vehicle.vin || vehicle.external_link) && (
                          <div className="ml-9 mb-3 space-y-1">
                            {vehicle.vin && (
                              <p className="text-xs text-muted-foreground flex items-center gap-2">
                                <Hash size={12} className="text-primary" />
                                <span className="font-medium">VIN/S/N:</span>
                                <span className="font-mono">{vehicle.vin}</span>
                              </p>
                            )}
                            {vehicle.external_link && (
                              <a 
                                href={vehicle.external_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:text-primary/80 flex items-center gap-2 transition-colors"
                              >
                                <ExternalLink size={12} />
                                <span>Mehr Infos zum Fahrzeug</span>
                              </a>
                            )}
                          </div>
                        )}
                        
                        <div className="ml-9 flex flex-wrap gap-2">
                          <button
                            onClick={() => toggleVisibility(vehicle)}
                            className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border transition-colors ${
                              vehicle.is_public !== false
                                ? 'border-green-500/30 bg-green-500/10 text-green-600 hover:bg-green-500/20'
                                : 'border-yellow-500/30 bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20'
                            }`}
                          >
                            {vehicle.is_public !== false ? (
                              <>
                                <Eye size={12} />
                                Sichtbar für Mitglieder
                              </>
                            ) : (
                              <>
                                <EyeOff size={12} />
                                Nur für mich sichtbar
                              </>
                            )}
                          </button>
                          
                          {/* Image Count Badge */}
                          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-border bg-muted/30 text-muted-foreground">
                            <Images size={12} />
                            {(vehicle.images || []).length} / 10 Bilder
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 sm:flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(vehicle)}
                          className="rounded-none"
                        >
                          <Edit2 size={14} className="mr-1" />
                          Bearbeiten
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(vehicle.id)}
                          className="rounded-none text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Image Gallery */}
                    {(vehicle.images && vehicle.images.length > 0) && (
                      <div className="mt-2">
                        <VehicleGallery 
                          images={vehicle.images}
                          vehicleName={vehicle.name}
                          canEdit={true}
                          onDelete={(imageId) => handleImageDelete(vehicle.id, imageId)}
                        />
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    {(vehicle.images || []).length < 10 && (
                      <div className="mt-2">
                        <input
                          type="file"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleImageUpload(vehicle.id, e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          id={`image-upload-${vehicle.id}`}
                          multiple={false}
                        />
                        <label
                          htmlFor={`image-upload-${vehicle.id}`}
                          className={`inline-flex items-center gap-2 text-sm px-4 py-2 border border-dashed border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 transition-colors cursor-pointer ${
                            uploadingImage === vehicle.id ? 'opacity-50 pointer-events-none' : ''
                          }`}
                        >
                          {uploadingImage === vehicle.id ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              Wird hochgeladen...
                            </>
                          ) : (
                            <>
                              <Upload size={16} />
                              Bild hinzufügen
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Info Box */}
        <div className="mt-8 p-4 bg-secondary/20 border border-border text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <Eye size={16} className="flex-shrink-0 mt-0.5" />
            <span>
              <strong>Sichtbarkeit:</strong> Fahrzeuge, die als "sichtbar" markiert sind, 
              können von anderen SHRT-Mitgliedern auf Ihrer Profilseite gesehen werden. 
              Private Fahrzeuge sehen nur Sie selbst.
            </span>
          </p>
        </div>
      </div>
    </Layout>
  );
}
